/** Types, intentions & métadonnées campagnes (V2/V3 — aligné backend). */

export const CAMPAIGN_KINDS = ["hero", "banner"];

export const CAMPAIGN_KIND_META = {
  hero: {
    label: "Hero",
    shortLabel: "Carrousel principal",
    description: "Message fort, grand visuel — même esprit qu’Amazon Deals / bannière d’accueil Leroy.",
    adminHint: "À réserver aux opérations majeures : le visuel occupe le hero.",
  },
  banner: {
    label: "Spotlight",
    shortLabel: "Spotlight accueil",
    description: "Carte premium après les catégories — image + texte + CTA, visible immédiatement.",
    adminHint: "Idéal pour pousser une offre, une catégorie ou un lien externe. Visible juste sous les catégories.",
  },
};

/** Intentions business (backend campaign_intent). */
export const CAMPAIGN_INTENTS = [
  "destockage",
  "saisonnier",
  "nouveaute",
  "operation_commerciale",
  "marque",
  "mise_en_avant",
];

export const INTENT_META = {
  destockage: {
    storefrontKicker: "Déstockage",
    sliderBadge: "Déstockage",
    cardClass: "ring-amber-500/25 border-amber-200/80",
    adminHint: "Priorité forte recommandée. CTA direct : « Dernières pièces », « Jusqu’à épuisement ».",
  },
  saisonnier: {
    storefrontKicker: "Saison",
    sliderBadge: "Saison",
    cardClass: "ring-sky-500/20 border-sky-200/70",
    adminHint: "Pensez à borner les dates début / fin pour retirer l’offre automatiquement.",
  },
  nouveaute: {
    storefrontKicker: "Nouveauté",
    sliderBadge: "Nouveau",
    cardClass: "ring-emerald-500/25 border-emerald-200/80",
    adminHint: "Mise en avant douce : mettez l’accent sur la découverte, pas la pression.",
  },
  operation_commerciale: {
    storefrontKicker: "Offre spéciale",
    sliderBadge: "Opération",
    cardClass: "ring-[#f7040a]/30 border-red-200/80",
    adminHint: "Idéal en hero : message unique, visuel fort, une action principale.",
  },
  marque: {
    storefrontKicker: "Marque",
    sliderBadge: "Marque",
    cardClass: "ring-[#190276]/25 border-[#190276]/20",
    adminHint: "Branding : logo / univers produit, lien vers catalogue filtré ou page marque.",
  },
  mise_en_avant: {
    storefrontKicker: "À découvrir",
    sliderBadge: "Sélection",
    cardClass: "ring-gray-300/60 border-gray-200",
    adminHint: "Usage standard : pousser une catégorie ou une sélection sans urgence artificielle.",
  },
};

/**
 * Objectifs affichés en étape 1 du wizard admin (mappe vers campaign_intent).
 * suggestKind / suggestImportance = pré-remplissage intelligent.
 */
export const WIZARD_BUSINESS_OBJECTIVES = [
  {
    intent: "destockage",
    label: "Déstocker",
    sub: "Écouler les surplus, CTA direct",
    suggestKind: "banner",
    suggestImportance: "high",
  },
  {
    intent: "nouveaute",
    label: "Lancer une nouveauté",
    sub: "Mettre en avant une gamme récente",
    suggestKind: "collection",
    suggestImportance: "normal",
  },
  {
    intent: "mise_en_avant",
    label: "Booster une catégorie",
    sub: "Univers produit, navigation catalogue",
    suggestKind: "collection",
    suggestImportance: "normal",
  },
  {
    intent: "operation_commerciale",
    label: "Opération commerciale",
    sub: "Semaine promo, événement fort",
    suggestKind: "hero",
    suggestImportance: "high",
  },
  {
    intent: "marque",
    label: "Mettre en avant une marque",
    sub: "Branding & confiance",
    suggestKind: "banner",
    suggestImportance: "normal",
  },
  {
    intent: "saisonnier",
    label: "Offre saisonnière",
    sub: "Chauffage, piscine, etc.",
    suggestKind: "banner",
    suggestImportance: "normal",
  },
];

export const IMPORTANCE_OPTIONS = [
  { value: "normal", label: "Standard" },
  { value: "high", label: "Mise en avant forte" },
];

export const VISIBILITY_OPTIONS = [
  { value: "homepage", label: "Accueil" },
  { value: "promos", label: "Page Promotions" },
  { value: "global", label: "Accueil + Promotions" },
];

export function campaignKindLabel(kind) {
  return CAMPAIGN_KIND_META[kind]?.label || kind;
}

export function intentMeta(intent) {
  return INTENT_META[intent] || INTENT_META.mise_en_avant;
}

/** Pastille hero (tag au-dessus du titre). */
export function storefrontHeroKicker(intent) {
  return intentMeta(intent).storefrontKicker;
}

export function intentSliderBadge(intent) {
  return intentMeta(intent).sliderBadge;
}

export function intentCardVisualClass(intent) {
  return intentMeta(intent).cardClass;
}

/** Ligne tag + source pour slider bas de page. */
export function sliderTagForPackRow(row) {
  const base = intentSliderBadge(row.campaign_intent);
  if (row.__source === "event") return `${base} · Événement`;
  return `${base} · Campagne`;
}

export function heroTagForCampaign(row) {
  if ((row.importance || "").toLowerCase() === "high") return "Prioritaire";
  return storefrontHeroKicker(row.campaign_intent);
}

export function campaignIntentLabel(intent) {
  return intentMeta(intent).sliderBadge;
}

/** Niveaux d'urgence vitrine (V4). */
export const URGENCY_LEVELS = ["none", "low", "medium", "high"];

export const URGENCY_LEVEL_OPTIONS = [
  { value: "none", label: "Aucune" },
  { value: "low", label: "Légère" },
  { value: "medium", label: "Modérée (offre limitée)" },
  { value: "high", label: "Forte (derniers jours / stock)" },
];

/** Texte badge urgence (combiné stock_hint si renseigné). */
export function storefrontUrgencyPrimary(row) {
  const hint = row.stock_hint && String(row.stock_hint).trim();
  if (hint) return hint;
  const lvl = (row.urgency_level || "none").toLowerCase();
  if (lvl === "none") return "";
  if (lvl === "low") return "Profitez-en vite";
  if (lvl === "medium") return "Offre limitée";
  if (lvl === "high") return "Derniers jours";
  return "";
}

/** ISO string pour countdown (API datetime ou string). */
export function urgencyEndToIso(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") return raw.includes("T") ? raw : `${raw}T23:59:59`;
  try {
    return new Date(raw).toISOString();
  } catch {
    return null;
  }
}
